import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, Signal } from '@angular/core';
import { ButtonModule, InputModule, SelectModule } from '@sag-ui';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AffectedItemsFormGroup, ItemOrQuantityInternationalFormGroup } from '../../../models/cod-create.model';
import { OrderLineTableComponent } from '../../../../shared/components/order-line-table/order-line-table.component';
import { IOrderLine, ItemOrQuantityIssueTypeInternational } from '@cod-monorepo/interfaces';
import { tap } from 'rxjs';
import { CodState } from '../../../state/cod.state';
import { select } from '@ngxs/store';

@Component({
    selector: 'app-cod-affected-items-quantity-international-step',
    templateUrl: './affected-items-quantity-international.component.html',
    imports: [
        CommonModule,
        SelectModule,
        InputModule,
        FormsModule,
        ReactiveFormsModule,
        ButtonModule,
        OrderLineTableComponent
    ],
    standalone: true
})
export class CodAffectedItemsQuantityInternationalStepComponent implements OnInit {
    @Input({ required: true })
    affectedItemsForm: FormGroup<AffectedItemsFormGroup>;
    @Input({ required: true }) form: FormGroup<ItemOrQuantityInternationalFormGroup>;

    public orderLineItems: Signal<IOrderLine[]> = select(CodState.orderLineItems);

    public issueTypes = ItemOrQuantityIssueTypeInternational;
    public hasReceivedUnorderedItem = false;

    ngOnInit(): void {
        this.initItemTypeChangeListener();
    }

    public get confirmed(): boolean {
        return this.affectedItemsForm.controls.confirmed.value ?? false;
    }

    public get lineItems(): IOrderLine[] {
        return this.affectedItemsForm.controls.affectedOrderLines.value ?? [];
    }

    public get disabled(): boolean {
        return this.affectedItemsForm.disabled;
    }

    public get valid(): boolean {
        const typeSelected = Boolean(this.form.controls.issueType.value);
        const orderLinesSelected = (this.affectedItemsForm.controls.affectedOrderLines.value ?? []).length > 0;
        const smn = (this.form.controls.unorderedItemSmn.value || '').toString().trim();
        const qty = this.form.controls.unorderedItemQuantity.value;

        const hasSmn = smn.length > 0;
        const hasQty = qty != null && !isNaN(qty) && Number(qty) > 0;

        return typeSelected && (this.hasReceivedUnorderedItem ? hasSmn && hasQty : orderLinesSelected);
    }

    public setConfirmed(): void {
        if (this.hasReceivedUnorderedItem) {
            const unorderedItemSmn = this.form.controls.unorderedItemSmn.value;
            const unorderedItemQuantity = this.form.controls.unorderedItemQuantity.value;
            const firstOrderLine = this.orderLineItems()?.[0];

            if (!firstOrderLine) {
                return;
            }

            this.affectedItemsForm.controls.affectedOrderLines.setValue([
                {
                    ...firstOrderLine,
                    salesOrderQuantity: unorderedItemQuantity ?? 0,
                    affectedQuantity: unorderedItemQuantity ?? 0,
                    materialDescription: `Unordered Item`,
                    price: 0,
                    salesOrderLine: 0,
                    handlingUnit: undefined,
                    smn: unorderedItemSmn ?? ''
                }
            ]);
        }

        this.affectedItemsForm.controls.confirmed.setValue(true);
    }

    public handleSelectionChange(value: IOrderLine[]): void {
        this.affectedItemsForm.controls.confirmed.setValue(false);
        this.affectedItemsForm.get('affectedOrderLines')?.setValue(value);
    }

    private initItemTypeChangeListener(): void {
        this.form.controls.issueType.valueChanges
            .pipe(
                tap(value => {
                    this.affectedItemsForm.controls.typeValid.setValue(!!value);
                    this.hasReceivedUnorderedItem =
                        value === ItemOrQuantityIssueTypeInternational.RECEIVED_UNORDERED_ITEM;
                })
            )
            .subscribe();
    }
}
